import React, { useState } from "react";
import { TextField, Modal, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Button, Typography, Chip,} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ListAltIcon from "@mui/icons-material/ListAlt";
import { parseDepth } from "../../utils/commonComponent/parse";
import { InwardhideColumn } from "../../global";

const InwardTestsViewer = ({ datasheetDetails1, inwardTestsList, updateInputObject, additionalTestDetails }) => {
  const [modalOpen, setModalOpen] = useState(false);

  const isSingle = !inwardTestsList || inwardTestsList.length <= 1;
  const singleData = isSingle
    ? inwardTestsList?.[0] || datasheetDetails1
    : null;

  const tableHeaders = [
    { label: "BH No./Trial Pit No.", key: "boreHoleNo" },
    { label: "Lot No.", key: "lotNumber" },
    { label: "Location/Chainage No./Source", key: "locationChainageNo" },
    { label: "From Depth", key: "fromDepth", parse: true },
    { label: "To Depth", key: "toDepth", parse: true },
    { label: "Sample Type", key: "sampleType" },
    { label: "Sample Condition", key: "sampleCondition" },
    { label: "Sample Details", key: "SamplingDetails" },
    { label: "Quantity of Sample", key: "quantity" },
    { label: "Length Of Rock", key: "lengthOfRock", parse: true },
  ];

  const visibleHeaders = tableHeaders.filter((header) => !InwardhideColumn.includes(header.key));

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <TextField
          label="Group"
          size="small"
          value={datasheetDetails1?.discipline_groupName || ""}
          fullWidth
          variant="outlined"
          disabled
        />
        <TextField
          label="Material"
          size="small"
          value={datasheetDetails1?.materialScope_MaterialName || ""}
          fullWidth
          variant="outlined"
          disabled
        />

        {isSingle ? (
          <>
            {visibleHeaders.map((header) => (
              <TextField
                key={header.key}
                label={header.label}
                size="small"
                value={
                  header.parse
                    ? parseDepth(
                        singleData?.[header.key] ??
                          datasheetDetails1?.[`inwardTests_${header.key}`]
                      )
                    : singleData?.[header.key] ??
                      datasheetDetails1?.[`inwardTests_${header.key}`] ??
                      ""
                }
                fullWidth
                variant="outlined"
                disabled
              />
            ))}
          </>
        ) : (
          <div className="flex items-center gap-3">
            <Button
              variant="outlined"
              size="small"
              startIcon={<ListAltIcon />}
              onClick={() => setModalOpen(true)}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              View +
            </Button>
          </div>
        )}
      </div>

      {/* Additional Test Details */}
      {additionalTestDetails?.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 my-3">
          {additionalTestDetails.map((label, index) => (
            <TextField
              key={index}
              label={label}
              size="small"
              fullWidth
              variant="outlined"
              value={
                datasheetDetails1?.observationSheet_additionalTestDetails?.[label] || ""
              }
              onChange={(e) => {
                const existing = datasheetDetails1?.observationSheet_additionalTestDetails || {};
                updateInputObject("observationSheet_additionalTestDetails", {
                  ...existing,
                  [label]: e.target.value,
                });
              }}
            />
          ))}
        </div>
      )}

      {/* Multiple Records Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: 950,
            bgcolor: "background.paper",
            boxShadow: "0px 8px 32px rgba(0,0,0,0.12)",
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 3,
              py: 2,
              borderBottom: "1px solid #e0e0e0",
              backgroundColor: "#fafafa",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <ListAltIcon sx={{ color: "#555", fontSize: 22 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#222", fontSize: "1rem" }}>
                Inward Test Details
              </Typography>
            </Box>

            <IconButton
              onClick={() => setModalOpen(false)}
              size="small"
              sx={{
                backgroundColor: "#f0f0f0",
                "&:hover": { backgroundColor: "#e0e0e0" },
              }}
            >
              <CloseIcon fontSize="small" sx={{ color: "#444" }} />
            </IconButton>
          </Box>

          {/* Table */}
          <TableContainer
            sx={{ maxHeight: 420, overflow: "auto", px: 2, pt: 2 }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    style={{
                      backgroundColor: "#f5f5f5",
                      fontWeight: 600,
                      textWrap: "nowrap",
                      borderRadius: "8px 0 0 8px",
                    }}
                  >
                    Sr.No.
                  </TableCell>
                  {visibleHeaders.map((header, idx) => (
                    <TableCell
                      key={header.key}
                      style={{
                        backgroundColor: "#f5f5f5",
                        fontWeight: 600,
                        textWrap: "nowrap",
                        borderRadius:
                          idx === visibleHeaders.length - 1
                            ? "0 8px 8px 0"
                            : undefined,
                      }}
                    >
                      {header.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {inwardTestsList.map((row, index) => (
                  <TableRow key={row.id}>
                    <TableCell sx={{ color: "#888", fontWeight: 500 }}>
                      {index + 1}
                    </TableCell>
                    {visibleHeaders.map((header) => (
                      <TableCell key={header.key} sx={{ color: "#333" }}>
                        {header.parse
                          ? parseDepth(row[header.key])
                          : row[header.key] || "-"}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <br/>
          <br/>
        </Box>
      </Modal>
    </>
  );
};

export default InwardTestsViewer;